import { memo, ReactNode } from "react";
import { Flex } from "../../../flex";
import { InfoTooltip } from "../../../info-tooltip";
import { Text } from "../../../text";
import { SpacingValue } from "../../../theme/box";
import { RedoListItem } from "../../list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../select-dropdown/redo-single-select-dropdown-input";

interface ChartCardBaseProps {
  title: string;
  /** Info icon next to the title, for context that doesn't fit in the title itself. */
  titleTooltip?: ReactNode;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  minHeight?: string;
  contentPadding?: SpacingValue;
}

interface ChartCardSingleProps extends ChartCardBaseProps {
  variant: "single";
}

interface ChartCardMultiTypeProps<T> extends ChartCardBaseProps {
  variant: "multiType";
  options: RedoListItem<T>[];
  selectedItem: RedoListItem<T> | undefined;
  onOptionSelected: (params: { item: RedoListItem<T> }) => void;
}

interface ChartCardWithSecondaryTextProps extends ChartCardBaseProps {
  variant: "withSecondaryText";
  secondaryText?: ReactNode;
}

export type ChartCardProps<T> =
  | ChartCardSingleProps
  | ChartCardMultiTypeProps<T>
  | ChartCardWithSecondaryTextProps;

export const ChartCard = memo(function ChartCard<T>({
  title,
  titleTooltip,
  subtitle,
  children,
  className,
  minHeight = "260px",
  contentPadding = "xl",
  ...props
}: ChartCardProps<T>) {
  return (
    <Flex
      bgColor="primary"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      className={className}
      dir="column"
      flex={1}
      style={{ minWidth: "400px" }}
      w="full"
    >
      <Flex
        align="center"
        borderColor="secondary"
        borderStyle="solid"
        justify="space-between"
        p="xl"
        style={{ borderBottomWidth: "1px" }}
        w="full"
      >
        <Flex dir="column" gap="xxs">
          <Flex align="center" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {title}
            </Text>
            {titleTooltip && (
              <InfoTooltip
                boxSize={14}
                tooltip={{ title: titleTooltip, leftAlign: true }}
              />
            )}
          </Flex>
          {subtitle && (
            <Text fontSize="xs" textColor="secondary">
              {subtitle}
            </Text>
          )}
        </Flex>
        {props.variant === "multiType" && (
          <RedoSingleSelectDropdownInput
            fitToAnchor={false}
            flexGrow={false}
            options={props.options}
            optionSelected={props.onOptionSelected}
            selectedItem={props.selectedItem}
            size="xs"
          />
        )}
        {props.variant === "withSecondaryText" &&
          props.secondaryText != null &&
          (typeof props.secondaryText === "string" ? (
            <Text fontSize="xs" textColor="secondary">
              {props.secondaryText}
            </Text>
          ) : (
            props.secondaryText
          ))}
      </Flex>
      <Flex flex="1" p={contentPadding} style={{ minHeight }}>
        {children}
      </Flex>
    </Flex>
  );
}) as <T>(props: ChartCardProps<T>) => ReactNode;
